import { Extensions } from '@tiptap/core';
import CodeBlock from '@tiptap/extension-code-block';
import { Color } from '@tiptap/extension-color';
import Document from '@tiptap/extension-document';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import TipTapTypography from '@tiptap/extension-typography';
import Youtube from '@tiptap/extension-youtube';
import StarterKit from '@tiptap/starter-kit';
import ImageResize from 'tiptap-extension-resize-image';

import { Callout } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/Callout';
import { FilingAwareBlockquote } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/FilingAwareBlockquote';
import { FilingQuoteText } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/FilingQuoteText';
import { FilingSourceCitation } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/FilingSourceCitation';
import { FilingSourceCitationBlock } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/FilingSourceCitationBlock';
import LinkEmbed from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/LinkEmbed';
import { createMemoTableExtensions } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/MemoTable';
import { ReportComponentEmbed } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/ReportComponentEmbed';
import { UnfurledLink } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/UnfurledLink';

interface CreateMemoReadOnlyExtensionsOptions {
    additionalExtensions?: Extensions;
    borderColor: string;
    dark: boolean;
}

export const createMemoReadOnlyExtensions = ({
    additionalExtensions = [],
    borderColor,
    dark,
}: CreateMemoReadOnlyExtensionsOptions): Extensions => [
    StarterKit.configure({
        document: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        link: {
            autolink: true,
            openOnClick: true,
            linkOnPaste: false,
            HTMLAttributes: {
                rel: 'noopener noreferrer',
                target: null,
            },
        },
        paragraph: false,
        text: false,
    }),
    Document,
    Paragraph,
    FilingQuoteText,
    FilingSourceCitationBlock,
    FilingSourceCitation,
    Text,
    TextStyle,
    Color,
    FilingAwareBlockquote.configure({
        HTMLAttributes: {
            class: dark ? 'blockquote-dark' : 'blockquote-light',
        },
    }),
    UnfurledLink,
    Highlight.configure({ multicolor: true }),
    CodeBlock,
    Callout.configure({
        types: ['info', 'warning', 'tip', 'danger'],
    }),
    TaskList,
    TaskItem.configure({
        nested: true,
    }),
    HorizontalRule.configure({
        HTMLAttributes: {
            style: `background-color: ${borderColor}; border-color: ${borderColor};`,
        },
    }),
    new LinkEmbed(),
    ReportComponentEmbed,
    Image,
    ImageResize,
    Youtube.configure({
        inline: true,
        modestBranding: true,
        progressBarColor: 'white',
        allowFullscreen: true,
        origin: import.meta.env.PROD ? 'https://dashboard.countercyclical.io' : 'http://localhost:3000',
        controls: true,
    }),
    TextAlign.configure({
        types: ['heading', 'paragraph'],
    }),
    TipTapTypography,
    ...createMemoTableExtensions(),
    ...additionalExtensions,
];
